import { FileText, Check, AlertCircle, Loader2, Clock } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import type { UploadStatus } from '@/api/upload'

interface UploadProgressProps {
  fileName: string
  status: UploadStatus
  progress: number
  error?: Error | null
  onCancel?: () => void
  onRetry?: () => void
}

/**
 * Status to icon and color mapping.
 */
const statusConfig: Record<UploadStatus, { icon: React.ElementType; color: string; label: string }> = {
  idle: { icon: Clock, color: 'text-muted-foreground', label: 'Waiting' },
  uploading: { icon: Loader2, color: 'text-blue-500', label: 'Uploading' },
  processing: { icon: Loader2, color: 'text-yellow-500', label: 'Processing' },
  complete: { icon: Check, color: 'text-green-500', label: 'Complete' },
  error: { icon: AlertCircle, color: 'text-destructive', label: 'Error' },
}

export function UploadProgress({
  fileName,
  status,
  progress,
  error,
  onCancel,
  onRetry,
}: UploadProgressProps) {
  const config = statusConfig[status]
  const Icon = config.icon
  const isAnimated = status === 'uploading' || status === 'processing'

  return (
    <div className="rounded-lg border p-4 space-y-3">
      {/* File info */}
      <div className="flex items-center gap-3">
        <FileText className="h-8 w-8 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{fileName}</p>
          <div className="flex items-center gap-2 text-sm">
            <Icon
              className={`h-4 w-4 ${config.color} ${isAnimated ? 'animate-spin' : ''}`}
            />
            <span className={config.color}>{config.label}</span>
            {status === 'uploading' && (
              <span className="text-muted-foreground">({progress}%)</span>
            )}
          </div>
        </div>
        {/* Actions */}
        {status === 'uploading' && onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        {status === 'error' && onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>

      {/* Progress bar */}
      {(status === 'uploading' || status === 'processing') && (
        <Progress
          value={status === 'uploading' ? progress : 100}
          className="h-2"
        />
      )}

      {/* Error message */}
      {status === 'error' && error && (
        <p className="text-sm text-destructive">
          {error.message || 'Upload failed. Please try again.'}
        </p>
      )}

      {/* Processing message */}
      {status === 'processing' && (
        <p className="text-sm text-muted-foreground">
          File uploaded successfully. Processing extraction...
        </p>
      )}
    </div>
  )
}
